import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import DOMPurify from 'dompurify';
import 'quill/dist/quill.snow.css';

const TOOLBAR_OPTIONS = [
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ color: [] }, { background: [] }],
  ['blockquote', 'link'],
  ['clean'],
];

export default function RichTextEditor({ value = '', onChange, placeholder = '' }) {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || editorRef.current) return undefined;

    const container = containerRef.current;
    const editorHost = document.createElement('div');
    container.appendChild(editorHost);
    const quill = new Quill(editorHost, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: TOOLBAR_OPTIONS },
    });
    quill.root.setAttribute('spellcheck', 'true');
    quill.root.setAttribute('lang', 'en-US');
    quill.root.setAttribute('autocorrect', 'on');
    quill.root.setAttribute('autocapitalize', 'sentences');
    editorRef.current = quill;
    if (initialValueRef.current) quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);

    const handleTextChange = () => {
      const html = quill.getSemanticHTML();
      onChangeRef.current?.(quill.getText().trim() ? html : '');
    };
    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      editorRef.current = null;
      container.innerHTML = '';
    };
  }, [placeholder]);

  useEffect(() => {
    const quill = editorRef.current;
    if (!quill) return;
    const currentHtml = quill.getText().trim() ? quill.getSemanticHTML() : '';
    if ((value || '') !== currentHtml) {
      const selection = quill.getSelection();
      quill.setText('');
      if (value) quill.clipboard.dangerouslyPasteHTML(value);
      if (selection) quill.setSelection(Math.min(selection.index, quill.getLength() - 1), 0, 'silent');
    }
  }, [value]);

  return <div className="claim-process-rich-editor" ref={containerRef} />;
}

export function RichTextContent({ value }) {
  const text = String(value || '');
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(text);
  if (!isHtml) return <p>{text || '-'}</p>;
  return <div className="claim-process-rich-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />;
}
